import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Divider, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Button } from '@/components/ui';
import type { RoleApiModel } from '@/superadmin/services/rolesService';
import type { PermissionApiModel } from '@/superadmin/services/permissionsService';

export interface RolePermissionMappingFormValues {
  role: string;
  permission: string;
}

export interface RolePermissionMappingFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: RolePermissionMappingFormValues) => void;
  roles: RoleApiModel[];
  permissions: PermissionApiModel[];
  loading?: boolean;
}

const drawerWidth = 480;

const RolePermissionMappingFormDrawer: React.FC<RolePermissionMappingFormDrawerProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  roles, 
  permissions,
  loading = false 
}) => {
  const [values, setValues] = useState<RolePermissionMappingFormValues>({
    role: '',
    permission: '',
  });

  useEffect(() => {
    if (open) {
      setValues({
        role: '',
        permission: '',
      });
    }
  }, [open]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.role || !values.permission) return;
    onSubmit(values);
  };

  const isFormValid = values.role && values.permission;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: drawerWidth } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography variant="h6">Map Role to Permission</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} px={2} py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          Role Permission Mapping
        </Typography>
        
        <FormControl fullWidth required>
          <InputLabel id="role-select-label">Select Role</InputLabel>
          <Select
            labelId="role-select-label"
            name="role"
            value={values.role}
            onChange={handleChange}
            label="Select Role"
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading roles...</Typography>
                </Box>
              </MenuItem>
            ) : (
              roles.map((role) => (
                <MenuItem key={role.uuid} value={role.uuid}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {role.role_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {role.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl fullWidth required>
          <InputLabel id="permission-select-label">Select Permission</InputLabel>
          <Select
            labelId="permission-select-label"
            name="permission"
            value={values.permission}
            onChange={handleChange}
            label="Select Permission"
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading permissions...</Typography>
                </Box>
              </MenuItem>
            ) : (
              permissions.map((permission) => (
                <MenuItem key={permission.uuid} value={permission.uuid}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {permission.permission_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {permission.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Box display="flex" gap={1} mt={1}>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={!isFormValid || loading}
            sx={{ flex: 1 }}
          >
            {loading ? 'Creating...' : 'Create Mapping'}
          </Button>
          <Button 
            type="button" 
            variant="outlined" 
            onClick={onClose} 
            disabled={loading}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default RolePermissionMappingFormDrawer;
